/**
 * 设备名尾缀归一：leidian14A / 雷电14A / zhxt_app:组:雷电14A → "14a"
 * 带连字符编号（雷电14-1）保留完整数字段，避免全都落成 "1"。
 */
export function deviceNameKey(raw) {
  const str = String(raw || '').trim().toLowerCase();
  if (!str) return '';
  const tail = str.includes(':') ? str.split(':').pop() : str;
  const hyphen = String(tail || '').match(/(\d+(?:[-_]\d+)*[a-z]*)$/i);
  if (hyphen) return hyphen[1].toLowerCase();
  const m = String(tail || '').match(/(\d+[a-z]*)$/i);
  return m ? m[1].toLowerCase() : String(tail || str).toLowerCase();
}


/**
 * Canonical member identity for mission roster / kanban / badges.
 * Prefer bind token (zhxt_app:…) → uid:… → device_unique_id → name.
 * stream_key / display name are aliases only (matched via missionIdMatchesMember).
 */
export function getCanonicalMemberId(member) {
  if (!member || typeof member !== 'object') return '';
  const deviceId = String(member.device_unique_id || '').trim();
  if (deviceId) return deviceId;
  const uid = String(member.uid || member.locus_uid || '').trim();
  if (uid) return uid.startsWith('uid:') ? uid : ('uid:' + uid);
  const name = String(member.name || '').trim();
  return name;
}

/** 任务中 id（令牌或 Locus id）是否对应该队员行 */
export function missionIdMatchesMember(missionId, member) {
  const mid = String(missionId || '').trim();
  if (!mid) return false;
  const ids = [member?.device_unique_id, member?.name, member?.deviceId, member?.id]
    .map((x) => String(x || '').trim())
    .filter(Boolean);
  if (ids.some((id) => id === mid)) return true;
  // stream_key / push_code are aliases to the person — never keep as membership id
  const sk = String(member?.stream_key || member?.streamKey || member?.push_code || '').trim();
  if (sk && sk === mid) return true;
  const midKey = deviceNameKey(mid);
  if (!midKey) return false;
  return ids.some((id) => deviceNameKey(id) === midKey);
}

/**
 * Raw bind/stream tokens (long hex etc.) are not people.
 * Avoid promoting cache/page-switch tokens into mission rows/counts.
 */
export function looksLikeGhostMissionId(raw) {
  const str = String(raw || '').trim();
  if (!str) return true;
  // Real roster keys — never treat as bind/stream tokens
  if (/^zhxt_app:/i.test(str)) return false;
  if (/^uid:/i.test(str)) return false;
  // Human 队员名 often has CJK / spaces / hyphenated display forms — keep those
  if (/[\u4e00-\u9fff\s]/.test(str) || /雷电|队员/.test(str)) return false;
  // 仅长 hex 绑定令牌当幽灵。勿用裸 [a-z0-9_]{6,64}：会误伤 leidian14A 等真实 device id，
  // 导致加入任务被丢弃、拖入分组后 resolveMember 藏人。stream_key 靠 missionIdMatchesMember 别名 remap。
  if (/^[a-f0-9]{16,64}$/i.test(str)) return true;
  return false;
}


export function mapMissionIdsToLiveMemberKeys(missionIds = [], members = []) {
  const list = Array.isArray(members) ? members : [];
  const out = [];
  const seen = new Set();
  (Array.isArray(missionIds) ? missionIds : []).forEach((rawId) => {
    const mid = String(rawId || '').trim();
    if (!mid) return;
    let best = null;
    let bestSeen = -1;
    list.forEach((m) => {
      if (!missionIdMatchesMember(mid, m)) return;
      const key = getCanonicalMemberId(m);
      if (!key) return;
      const t = m?.last_seen ? new Date(m.last_seen).getTime() : 0;
      const score = Number.isFinite(t) ? t : 0;
      if (!best || score > bestSeen) {
        best = key;
        bestSeen = score;
      }
    });
    const chosen = best || mid;
    if (seen.has(chosen)) return;
    seen.add(chosen);
    out.push(chosen);
  });
  return out;
}

/** 是否像绑定令牌 device_unique_id（zhxt_app:小组:姓名） */
/**
 * Mission ids -> live member keys (deduped).
 * Unlike mapMissionIdsToLiveMemberKeys: drop unmatched (ghost) ids; do not fall back to raw id.
 * When members is empty, return trimmed ids unchanged so we do not wipe before list loads.
 */
export function liveMissionMemberKeys(missionIds = [], members = []) {
  const list = Array.isArray(members) ? members : [];
  const raw = Array.isArray(missionIds) ? missionIds : [];
  // Count / badge: no roster → 0 (do not inflate with cached tokens)
  if (!list.length) return [];
  const out = [];
  const seen = new Set();
  raw.forEach((rawId) => {
    const mid = String(rawId || '').trim();
    if (!mid) return;
    let bestKey = null;
    let bestMember = null;
    let bestSeen = -1;
    list.forEach((m) => {
      if (!missionIdMatchesMember(mid, m)) return;
      const key = getCanonicalMemberId(m);
      if (!key) return;
      const t = m?.last_seen ? new Date(m.last_seen).getTime() : 0;
      const score = Number.isFinite(t) ? t : 0;
      if (!bestKey || score > bestSeen) {
        bestKey = key;
        bestMember = m;
        bestSeen = score;
      }
    });
    if (!bestKey || seen.has(bestKey)) return;
    seen.add(bestKey);
    out.push(bestKey);
  });
  return out;
}

/**
 * Persist/reconcile mission ids:
 * - Remap aliases (name / zhxt_app / stream_key) → live device key
 * - Drop ghost tokens that match nobody
 * - Keep unmatched non-ghost ids (offline people who left the roster)
 * - members empty → drop only obvious token ghosts; keep other ids
 */
export function sanitizeMissionMemberIds(missionIds = [], members = []) {
  const list = Array.isArray(members) ? members : [];
  const raw = Array.isArray(missionIds) ? missionIds : [];
  if (!list.length) {
    // Roster not ready: keep ids (avoid wipe on page switch). UI hides ghost-like stubs.
    const seen = new Set();
    const out = [];
    raw.forEach((rawId) => {
      const mid = String(rawId || '').trim();
      if (!mid || seen.has(mid)) return;
      seen.add(mid);
      out.push(mid);
    });
    return out;
  }
  const out = [];
  const seen = new Set();
  raw.forEach((rawId) => {
    const mid = String(rawId || '').trim();
    if (!mid) return;
    let best = null;
    let bestSeen = -1;
    list.forEach((m) => {
      if (!missionIdMatchesMember(mid, m)) return;
      // Roster hit = real person；token 行若仅 stream_key 展示名仍可 remap 到 device key
      const key = getCanonicalMemberId(m);
      if (!key) return;
      const t = m?.last_seen ? new Date(m.last_seen).getTime() : 0;
      const score = Number.isFinite(t) ? t : 0;
      if (!best || score > bestSeen) {
        best = key;
        bestSeen = score;
      }
    });
    if (best) {
      if (seen.has(best)) return;
      seen.add(best);
      out.push(best);
      return;
    }
    // Unmatched with roster present: drop token ghosts; keep offline real identities
    if (looksLikeGhostMissionId(mid)) return;
    if (seen.has(mid)) return;
    seen.add(mid);
    out.push(mid);
  });
  return out;
}


export function countLiveMissionMembers(missionIds = [], members = []) {
  return liveMissionMemberKeys(missionIds, members).length;
}

export function isTokenLikeId(raw) {
  const str = String(raw || '').trim();
  return /^zhxt_app:/i.test(str);
}

/**
 * 列表/地图展示名：只用可读 name，不把 zhxt_app:小组:… 整串当标题。
 * 若 name 已被写成令牌 id，取最后一段。
 */
export function getMemberDisplayName(member) {
  const deviceId = String(member?.device_unique_id || '').trim();
  const rawName = String(member?.name || '').trim();
  const pickTail = (s) => {
    if (!s || !s.includes(':')) return s;
    const tail = s.split(':').filter(Boolean).pop();
    return String(tail || s).trim();
  };
  if (rawName) {
    if (isTokenLikeId(rawName) || (deviceId && rawName === deviceId && deviceId.includes(':'))) {
      return pickTail(rawName) || rawName;
    }
    return rawName;
  }
  if (deviceId) return isTokenLikeId(deviceId) ? (pickTail(deviceId) || deviceId) : deviceId;
  return '未知';
}

/**
 * 地图角标：自研 App → app；Locus（及其他）→ lc。
 * 优先 client_source；缺省时按 device_unique_id 前缀推断。
 */
/** True when display name is a real 队员名, not a bare bind/stream token. */
export function memberHasHumanDisplayName(member) {
  const dn = String(getMemberDisplayName(member) || '').trim();
  if (!dn || dn === '未知') return false;
  // 仅拒绝「展示名本身是长 hex」；leidian14A 等设备名算可读
  if (/^[a-f0-9]{16,64}$/i.test(dn)) return false;
  // stream_key / push_code alone is not a person label
  const sk = String(member?.stream_key || member?.streamKey || member?.push_code || '').trim();
  if (sk && dn === sk) return false;
  return true;
}


export function getMemberSourceBadge(member) {
  const src = String(member?.client_source || '').trim().toLowerCase();
  if (src === 'zhxt_app') return 'app';
  if (src === 'locus') return 'lc';
  if (isTokenLikeId(member?.device_unique_id)) return 'app';
  return 'lc';
}

/**
 * 指挥端列表去重：
 * 1) 已有 uid:* 时丢弃同名 zhxt_app / name-as-id 幽灵；
 * 2) 同展示名多行时只留一行（优先 uid:*，其次 last_seen 最新）。
 */
export function dedupeMembersForCommanderList(members = []) {
  const list = Array.isArray(members) ? members.slice() : [];
  const uidNames = new Set();
  list.forEach((d) => {
    const id = String(d?.device_unique_id || '').trim();
    if (!/^uid:/i.test(id)) return;
    const n = String(d?.name || '').trim();
    if (n) uidNames.add(n);
  });
  const withoutAlias = uidNames.size === 0
    ? list
    : list.filter((d) => {
      const id = String(d?.device_unique_id || '').trim();
      if (/^uid:/i.test(id)) return true;
      const n = String(d?.name || '').trim();
      if (n && uidNames.has(n)) return false;
      if (uidNames.has(id)) return false;
      if (isTokenLikeId(id)) {
        const tail = id.split(':').filter(Boolean).pop();
        if (tail && uidNames.has(String(tail).trim())) return false;
      }
      return true;
    });

  const scoreOf = (d) => {
    const id = String(d?.device_unique_id || '').trim();
    const uidBoost = /^uid:/i.test(id) ? 1e15 : 0;
    const t = d?.last_seen ? new Date(d.last_seen).getTime() : 0;
    return uidBoost + (Number.isFinite(t) ? t : 0);
  };
  const bestByName = new Map();
  withoutAlias.forEach((d) => {
    const n = String(d?.name || '').trim();
    if (!n) return;
    const prev = bestByName.get(n);
    if (!prev || scoreOf(d) >= scoreOf(prev)) bestByName.set(n, d);
  });
  if (bestByName.size === 0) return withoutAlias;
  const keep = new Set(bestByName.values());
  return withoutAlias.filter((d) => {
    const n = String(d?.name || '').trim();
    if (!n) return true;
    return keep.has(d);
  });
}
